import React, { useEffect, useRef, useState, useCallback } from "react";
import { useField } from "@unform/core";
import { MdError } from "react-icons/md";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  CustomDatePicker,
  DEFAULT_DATE_PICKER_THEME,
  Error,
  RootContainer,
  Label,
  InputRow,
  LabelContainer,
  AsteriskText,
  Tooltip,
} from "./styles";
import InfoTooltip from "../InfoTooltip";

dayjs.extend(customParseFormat);

const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";

/**
 * Unform-integrated single date picker built on antd 4.x DatePicker + dayjs.
 * Keeps the same public API of `DatePickerWithLabel`, exposing/receiving
 * plain JS `Date` values so it can later replace it as a drop-in component.
 * Pass `theme` with any of `DEFAULT_DATE_PICKER_THEME` keys to match a
 * different form's colors/sizes without touching this component.
 */
const DatePickerAntdWithLabel = ({
  name,
  label,
  labelWidth,
  className,
  maxWidth,
  labelOrientation,
  required,
  tooltip,
  validationBlur,
  disabled,
  format = DEFAULT_DATE_FORMAT,
  minDate,
  maxDate,
  disabledDate,
  theme,
  ...rest
}) => {
  const pickerTheme = { ...DEFAULT_DATE_PICKER_THEME, ...theme };
  const datePickerRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(false);

  const { fieldName, defaultValue, error, registerField } = useField(name);
  const [date, setDate] = useState(defaultValue ? dayjs(defaultValue) : null);

  // Register the field with Unform, exposing/receiving plain JS Date values
  useEffect(() => {
    registerField({
      name: fieldName,
      ref: datePickerRef,
      getValue: (ref) => (ref.current ? ref.current.toDate() : null),
      setValue: (ref, value) => {
        const parsedValue = value ? dayjs(value) : null;
        ref.current = parsedValue;
        setDate(parsedValue);
      },
      clearValue: (ref) => {
        ref.current = null;
        setDate(null);
      },
    });
  }, [fieldName, registerField]);

  // Update the date when defaultValue changes
  useEffect(() => {
    const parsedValue = defaultValue ? dayjs(defaultValue) : null;
    datePickerRef.current = parsedValue;
    setDate(parsedValue);
  }, [defaultValue]);

  // Handle input blur
  const handleInputBlur = useCallback(() => {
    setIsFilled(!!date);
    if (validationBlur) {
      validationBlur(fieldName);
    }
  }, [date, fieldName, validationBlur]);

  // Handle date change
  const handleDateChange = (value) => {
    datePickerRef.current = value;
    setDate(value);
  };

  // Handle blur
  const handleBlur = () => {
    setIsFocused(false);
    handleInputBlur();
  };

  const handleDisabledDate = (current) => {
    if (disabledDate) return disabledDate(current);
    if (!current) return false;
    if (minDate && current.isBefore(dayjs(minDate), "day")) return true;
    if (maxDate && current.isAfter(dayjs(maxDate), "day")) return true;
    return false;
  };

  return (
    <RootContainer className={className} maxWidth={maxWidth}>
      <InputRow labelOrientation={labelOrientation}>
        <LabelContainer labelWidth={labelWidth}>
          <Label labelWidth={labelWidth} labelOrientation={labelOrientation}>
            {label}
            {required && <AsteriskText>*</AsteriskText>}
          </Label>
          {tooltip && (
            <Tooltip>
              <InfoTooltip {...tooltip} />
            </Tooltip>
          )}
        </LabelContainer>
        <CustomDatePicker
          value={date}
          format={format}
          onChange={handleDateChange}
          onBlur={handleBlur}
          onFocus={() => setIsFocused(true)}
          disabled={disabled}
          disabledDate={
            disabledDate || minDate || maxDate ? handleDisabledDate : undefined
          }
          $theme={pickerTheme}
          $isFocused={isFocused}
          $isFilled={isFilled}
          $isErrored={!!error}
          {...rest}
        />
      </InputRow>

      {error && (
        <Error title={error}>
          <MdError color="#e00000" size={20} />
          {error}
        </Error>
      )}
    </RootContainer>
  );
};

export default DatePickerAntdWithLabel;
